import { useState } from "react";
import { getConsent, setConsent } from "@/lib/analytics";
import { cn } from "@/lib/utils";
import { Button } from "./ui/shadcn/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "./ui/shadcn/dialog";

export function CookieConsentActions({
  onChoose = setConsent,
}: {
  onChoose?: (choice: "accepted" | "rejected") => void;
}) {
  return (
    <div className="flex flex-col gap-3 min-[380px]:flex-row">
      <Button variant="outline" onClick={() => onChoose("rejected")}>
        Reject analytics
      </Button>
      <Button onClick={() => onChoose("accepted")}>Allow analytics</Button>
    </div>
  );
}

/** An in-flow way to revisit consent; never covers navigation or page actions. */
export function CookiePreferencesButton({ className }: { className?: string }) {
  const [open, setOpen] = useState(false);
  const [consent, setCurrentConsent] =
    useState<ReturnType<typeof getConsent>>(null);

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (next) setCurrentConsent(getConsent());
        setOpen(next);
      }}
    >
      <DialogTrigger asChild>
        <button
          type="button"
          className={cn(
            "w-fit text-sm font-medium text-brand-ink underline-offset-4 hover:underline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-primary",
            className,
          )}
        >
          Cookie preferences
        </button>
      </DialogTrigger>
      <DialogContent
        hideLogo
        className="h-auto w-[calc(100%-2rem)] rounded-xl sm:h-auto sm:max-w-md"
      >
        <DialogHeader className="pt-5">
          <DialogTitle>Cookie preferences</DialogTitle>
          <DialogDescription>
            Essential cookies keep the app working. Analytics cookies are
            optional and help us improve visits and feature usage.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-5 px-6 py-6">
          <p className="text-sm text-muted-foreground" role="status">
            {consent === "accepted"
              ? "Analytics is currently allowed."
              : consent === "rejected"
                ? "Analytics is currently rejected."
                : "You haven't chosen an analytics preference yet."}
          </p>
          <a
            href="https://rentinit.ae/cookie-policy/"
            target="_blank"
            rel="noreferrer"
            className="inline-block text-sm text-brand-ink underline underline-offset-4"
          >
            How we use cookies
          </a>
          <CookieConsentActions
            onChoose={(choice) => {
              setConsent(choice);
              setOpen(false);
            }}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
}
